// AGENT-A6: Vendor multi-select with popover. Reads vendor list from the
// "filters" react-query (A5 hook over /api/dashboard/filters). Empty list
// renders an explicit "(no vendors available)" message — this addresses the
// user-reported bug where the popover read "No data" with no signal whether
// the request had failed or simply returned zero rows.

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../api/client";
import type { FiltersResponse } from "../types";
import { useFilterStore } from "../state/filterStore";

interface PopoverPosition {
  top: number;
  left: number;
  width: number;
}

export function VendorMultiselect(): JSX.Element {
  const vendors = useFilterStore((s) => s.vendors);
  const toggleVendor = useFilterStore((s) => s.toggleVendor);

  const filtersQuery = useQuery<FiltersResponse>({
    queryKey: ["filters"],
    queryFn: ({ signal }) =>
      api.get<FiltersResponse>("/dashboard/filters", signal),
    staleTime: 5 * 60_000,
  });

  const vendorOptions = useMemo(
    () => filtersQuery.data?.vendors ?? [],
    [filtersQuery.data?.vendors],
  );

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const popRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<PopoverPosition | null>(null);

  // Position the popover anchored to the button. Recomputed on open /
  // viewport changes.
  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    const btn = buttonRef.current;
    if (!btn) return;
    const place = () => {
      const r = btn.getBoundingClientRect();
      setPos({
        top: r.bottom + 4,
        left: r.left,
        width: Math.max(r.width, 260),
      });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open]);

  // Close on outside click + escape.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (popRef.current?.contains(t)) return;
      if (buttonRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return vendorOptions;
    return vendorOptions.filter((v) => v.name.toLowerCase().includes(q));
  }, [vendorOptions, search]);

  const selectedCount = vendors.size;
  const buttonLabel =
    selectedCount === 0
      ? "Vendor"
      : selectedCount === 1
        ? `Vendor (1 selected)`
        : `Vendor (${selectedCount} selected)`;

  const onSelectAll = useCallback(() => {
    // Use Zustand setState directly so we don't have to add a new action
    // to A5's filterStore. This is read-only with respect to its public
    // action surface — we replace the `vendors` Set wholesale.
    useFilterStore.setState({
      vendors: new Set(filtered.map((v) => v.name)),
    });
  }, [filtered]);

  const onClear = useCallback(() => {
    useFilterStore.setState({ vendors: new Set<string>() });
  }, []);

  // ────────────────────────────────────────────────────────────────
  // Render
  // ────────────────────────────────────────────────────────────────

  const popover =
    open && pos
      ? createPortal(
          <div
            ref={popRef}
            className="dash-vendor-popover dash-card"
            style={{
              position: "fixed",
              top: pos.top,
              left: pos.left,
              width: pos.width,
            }}
            role="dialog"
            aria-label="Vendor filter"
          >
            <input
              type="text"
              className="dash-control dash-vendor-popover__search"
              placeholder="Search vendors…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
            />
            <div className="dash-vendor-popover__list">
              {filtersQuery.isLoading ? (
                <div className="dash-vendor-popover__empty dash-small">
                  Loading…
                </div>
              ) : filtersQuery.isError ? (
                <div className="dash-vendor-popover__empty dash-small">
                  Failed to load vendors.
                </div>
              ) : vendorOptions.length === 0 ? (
                <div className="dash-vendor-popover__empty dash-small">
                  (no vendors available)
                </div>
              ) : filtered.length === 0 ? (
                <div className="dash-vendor-popover__empty dash-small">
                  No vendors match "{search}".
                </div>
              ) : (
                filtered.map((v) => {
                  const checked = vendors.has(v.name);
                  return (
                    <label
                      key={v.name}
                      className="dash-vendor-popover__row"
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleVendor(v.name)}
                      />
                      <span className="dash-vendor-popover__name">
                        {v.name}
                      </span>
                      <span className="dash-vendor-popover__count dash-subtle dash-num">
                        {v.count}
                      </span>
                    </label>
                  );
                })
              )}
            </div>
            <div className="dash-vendor-popover__footer">
              <button
                type="button"
                className="dash-button"
                onClick={onSelectAll}
                disabled={filtered.length === 0}
              >
                Select all
              </button>
              <button
                type="button"
                className="dash-button"
                onClick={onClear}
                disabled={selectedCount === 0}
              >
                Clear
              </button>
            </div>
          </div>,
          document.body,
        )
      : null;

  return (
    <div className="dash-filter-field">
      <span className="dash-filter-field__header">Vendor</span>
      <button
        ref={buttonRef}
        type="button"
        className="dash-vendor-button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <span className="dash-vendor-button__label">{buttonLabel}</span>
        <span aria-hidden className="dash-vendor-button__caret">
          {open ? "▴" : "▾"}
        </span>
      </button>
      {popover}
    </div>
  );
}
